/**
 * Gallery API client — CRUD for AI-generated images and folders.
 * Uses JWT auth via the shared getAuthHeader() from client.js.
 */

import { API_URL, getAuthHeader } from './client';

async function galleryFetch(path, options = {}) {
  const url = `${API_URL}${path}`;
  const headers = {
    ...options.headers,
    Authorization: getAuthHeader(),
  };

  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData) && !(options.body instanceof Blob)) {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(options.body);
  }

  let res = await fetch(url, { ...options, headers });

  // Auto-refresh JWT on 401 (single-flight via useAuthStore.refreshAccessToken).
  if (res.status === 401 && headers.Authorization?.startsWith('Bearer ')) {
    try {
      const { useAuthStore } = await import('../store/useAuthStore');
      const newToken = await useAuthStore.getState().refreshAccessToken();
      if (newToken) {
        headers.Authorization = 'Bearer ' + newToken;
        res = await fetch(url, { ...options, headers });
      }
    } catch (e) { /* fall through */ }
  }

  if (res.status === 401) {
    const err = new Error('Votre session a expiré. Reconnectez-vous, puis relancez l\'opération.');
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    // Le backend rédige déjà un message destiné à l'utilisateur dans `detail`
    // (quota de stockage, dossier introuvable…). On le remonte tel quel, sinon
    // il n'atteint jamais l'écran. Le code HTTP part sur `err.status`, pas dans
    // le texte lu : c'est une information d'exploitation, pas un message.
    let message = 'L\'opération sur la galerie n\'a pas abouti. Réessayez dans quelques instants.';
    try {
      const j = await res.json();
      if (j && j.detail) message = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
    } catch (_) { /* corps non JSON : on garde le message générique */ }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;

  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  return res;
}


// ─── Images ──────────────────────────────────────

/**
 * List gallery images with filters, sorting, and pagination.
 * @param {Object} params - { page, pageSize, folderId, search, sort, order, workflowType, favoritesOnly }
 */
export async function listGalleryImages(params = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', params.page);
  if (params.pageSize) qs.set('page_size', params.pageSize);
  if (params.folderId) qs.set('folder_id', params.folderId);
  if (params.search) qs.set('search', params.search);
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  if (params.workflowType) qs.set('workflow_type', params.workflowType);
  if (params.favoritesOnly) qs.set('favorites_only', 'true');
  return galleryFetch(`/api/me/gallery?${qs.toString()}`);
}

/**
 * Save a generated image to the gallery.
 * @param {Object} data - { imageB64, filename, originalPrompt, workflowType, metadataJson, folderId }
 */
export async function saveToGallery(data) {
  return galleryFetch('/api/me/gallery', {
    method: 'POST',
    body: {
      image_b64: data.imageB64,
      filename: data.filename || '',
      original_prompt: data.originalPrompt || null,
      workflow_type: data.workflowType || 't2i',
      metadata_json: data.metadataJson || {},
      folder_id: data.folderId || null,
    },
  });
}

/**
 * Get a single gallery image with presigned URLs.
 */
export async function getGalleryImage(id) {
  return galleryFetch(`/api/me/gallery/${id}`);
}

/**
 * Update an image (rename, favorite, move to folder).
 * @param {string} id - Image UUID
 * @param {Object} updates - { filename, isFavorite, folderId, moveToRoot }
 */
export async function updateGalleryImage(id, updates) {
  return galleryFetch(`/api/me/gallery/${id}`, {
    method: 'PUT',
    body: {
      filename: updates.filename ?? undefined,
      is_favorite: updates.isFavorite ?? undefined,
      folder_id: updates.folderId ?? undefined,
      move_to_root: updates.moveToRoot ?? false,
    },
  });
}

/**
 * Delete a single image.
 */
export async function deleteGalleryImage(id) {
  return galleryFetch(`/api/me/gallery/${id}`, { method: 'DELETE' });
}

/**
 * Batch operation on multiple images (move, delete, download).
 * @param {Object} data - { ids, operation, targetFolderId }
 */
/**
 * Base64 d'une image de la galerie.
 *
 * Passe par `galleryFetch`, et c'est le point : deux composants appelaient
 * `fetch` en direct avec l'en-tête d'autorisation, sans la danse
 * « 401 → rafraîchissement → nouvel essai » que le CLAUDE.md impose à tout
 * client d'API. Un jeton qui expirait pendant l'insertion d'un lot de dix
 * images faisait échouer le lot entier.
 */
export async function fetchGalleryImageBase64(id) {
  return galleryFetch(`/api/me/gallery/${id}/base64`);
}

export async function getStorageUsage() {
  return galleryFetch('/api/me/storage');
}

export async function batchGalleryOperation(data) {
  return galleryFetch('/api/me/gallery/batch', {
    method: 'POST',
    body: {
      ids: data.ids,
      operation: data.operation,
      target_folder_id: data.targetFolderId || null,
    },
  });
}

/**
 * Download multiple images as a ZIP file.
 * @param {string[]} ids - Image UUIDs
 * @returns {Promise<Blob>} ZIP file blob
 */
export async function downloadGalleryZip(ids) {
  const res = await galleryFetch('/api/me/gallery/download-zip', {
    method: 'POST',
    body: { ids, operation: 'download' },
  });
  // res is a Response object (not JSON)
  return res.blob();
}


// ─── Folders ──────────────────────────────────────

/**
 * List folders at a given level.
 * @param {string|null} parentId - Parent folder UUID or null for root
 */
export async function listGalleryFolders(parentId = null) {
  const qs = parentId ? `?parent_id=${parentId}` : '';
  return galleryFetch(`/api/me/gallery/folders${qs}`);
}

/**
 * Create a new folder.
 * @param {Object} data - { name, parentId }
 */
export async function createGalleryFolder(data) {
  return galleryFetch('/api/me/gallery/folders', {
    method: 'POST',
    body: { name: data.name, parent_id: data.parentId || null },
  });
}

/**
 * Rename a folder.
 * @param {string} id - Folder UUID
 * @param {string} name - New name
 */
export async function renameGalleryFolder(id, name) {
  return galleryFetch(`/api/me/gallery/folders/${id}`, {
    method: 'PUT',
    body: { name },
  });
}

/**
 * Delete a folder.
 * @param {string} id - Folder UUID
 * @param {boolean} deleteContents - Also delete all images inside
 */
export async function deleteGalleryFolder(id, deleteContents = false) {
  const qs = deleteContents ? '?delete_contents=true' : '';
  return galleryFetch(`/api/me/gallery/folders/${id}${qs}`, { method: 'DELETE' });
}
